import { z } from 'zod';
import { memberIdSchema } from '../identity/legacy-identity-id';
import { apiEnvelope } from '../envelope';
import { ianaTimezoneSchema } from '../timezone';

// Wire + config contracts for the rewards (contingency-management) resource.
//
// The signed-ledger vocabulary (`REWARD_OPERATION_TYPE` / `REWARD_OPERATION_SUBTYPE`
// below) is the single source of truth (ADR-056 Rule 8): the reshaped
// `reward_ledger_entries` CHECKs mirror it and the member-facing wire schemas
// derive from it. The ledger is append-only, audited money movement; the balance
// is a derived `SUM(amount)` aggregate, never a stored column.

// Legacy category vocabulary. Retained ONLY for the internal-rewards staff
// summary wire (Atlas reads `entryType`): the reshaped signed ledger no longer
// stores it, so the staff projection derives it from `operation_type`
// (reward → earned, redemption → issued, redemption_refund → refunded). The
// member-facing ledger + the DB use the operation_type/operation_subtype pair
// below.
//
// Adding a value here is ADR-135-safe precisely because this enum is NOT on any
// client-parsed member response — it is read only by the Atlas staff panel (a
// web SPA deployed in lockstep with the API, never a pre-tolerance mobile
// bundle). The member ledger wire carries `operationSubtype: z.string()` instead,
// so the mobile client distinguishes a refund through its own tolerant registry.
export const REWARD_ENTRY_TYPE = ['earned', 'issued', 'reversed', 'refunded'] as const;
export const rewardEntryTypeSchema = z.enum(REWARD_ENTRY_TYPE);
export type RewardEntryType = z.infer<typeof rewardEntryTypeSchema>;

// Signed-ledger operation vocabulary (single source of truth, ADR-056 Rule 8;
// mirrored by the `reward_ledger_entries` CHECKs). `operation_type` is the
// money direction (`reward` credit ≥ 0; `redemption` debit ≤ 0, reserved for
// the redemption backend); `operation_subtype` is the specific earn behaviour. Appointment /
// manual values are reserved in the enum now so the follow-ups (appointments) need no
// further ledger migration.
export const REWARD_OPERATION_TYPE = ['reward', 'redemption'] as const;
export const rewardOperationTypeSchema = z.enum(REWARD_OPERATION_TYPE);
export type RewardOperationType = z.infer<typeof rewardOperationTypeSchema>;

export const REWARD_OPERATION_SUBTYPE = [
  'first_daily_check_in',
  'attended_appointment',
  'first_attended_appointment',
  // Staff-authored positive credits (not earn-rule behaviours): `manual_reward`
  // is a goodwill/compensation grant, `admin_adjustment` a correction of an
  // erroneous entry. Both are minted only by the internal staff grant path,
  // never by an earn rule, but they carry the earn `operation_type='reward'`
  // sign, so they live beside the earn behaviours rather than in the
  // redemption/legacy tail of the ledger-only set.
  'manual_reward',
  'admin_adjustment',
  // A CM v3 earn behaviour Galapagos has not natively implemented yet; it is
  // an earn behaviour all the same, so it belongs to the narrow vocabulary an
  // earn rule's `behavior` and the ways-to-earn wire may name.
  'verified_test_result',
] as const;
export const rewardOperationSubtypeSchema = z.enum(REWARD_OPERATION_SUBTYPE);
export type RewardOperationSubtype = z.infer<typeof rewardOperationSubtypeSchema>;

// The full ledger `operation_subtype` CHECK vocabulary — wider than the earn
// behaviours above. It adds the redemption debit/refund subtypes and the
// backend2 import vocabulary. Kept separate so an earn rule's `behavior` (which
// uses `rewardOperationSubtypeSchema`) can never reference a redemption or
// legacy subtype, while the ledger column and its CHECK accept the full set.
export const REWARD_LEDGER_OPERATION_SUBTYPE = [
  ...REWARD_OPERATION_SUBTYPE,
  // Redemption debits and their refunds: ledger-only, never an earn behaviour.
  'product_redemption',
  'redemption_refund',
  // Pelago 1.0 behaviours the member migration import lands 1:1 (signed,
  // source_ref-keyed). No Galapagos earn rule mints them and the ways-to-earn
  // wire never names them; the member-facing ledger wire serves them as an
  // open string.
  'legacy_check_in',
  'legacy_cbt_step',
  'legacy_strength_survey',
  'legacy_completed_intention',
  'legacy_joined_appointment',
  // backend2's one-off Year 2 renewal reassessment reward (`y2CheckInCompleted`).
  'legacy_y2_check_in',
  // backend2's coarse import vocabulary, kept beside the fine-grained one above
  // until the two import paths are reconciled.
  'legacy_v1_reward',
  'legacy_cmv3_balance',
  'legacy_redemption',
] as const;
export const rewardLedgerOperationSubtypeSchema = z.enum(REWARD_LEDGER_OPERATION_SUBTYPE);
export type RewardLedgerOperationSubtype = z.infer<typeof rewardLedgerOperationSubtypeSchema>;

// Redemption commerce vocabulary (product discriminator + order lifecycle).
export const REWARD_PRODUCT_TYPE = ['e_gift', 'gift_item'] as const;
export const rewardProductTypeSchema = z.enum(REWARD_PRODUCT_TYPE);
export type RewardProductType = z.infer<typeof rewardProductTypeSchema>;

export const REWARD_REDEMPTION_STATUS = ['processing', 'sent', 'cancelled'] as const;
export const rewardRedemptionStatusSchema = z.enum(REWARD_REDEMPTION_STATUS);
export type RewardRedemptionStatus = z.infer<typeof rewardRedemptionStatusSchema>;

// Why an order's last vendor send failed, stored on the order. Closed and
// vendor-neutral: the vendor's own text never lands here, because it can echo
// the recipient. Only `insufficient_funds` is a refusal the vendor guarantees
// created nothing, and the fulfilment provider seam owns that rule; every other
// value is recorded so a person reconciling the order knows why it stopped.
export const REWARD_SEND_FAILURES = [
  'insufficient_funds',
  'unauthorised',
  'rate_limited',
  'vendor_error',
  'unrecognised',
  'unavailable',
  'timeout',
  'connection_error',
  'no_send_ref',
  'token_unavailable',
  'pre_send_invalid',
  'unknown',
] as const;
export const rewardSendFailureSchema = z.enum(REWARD_SEND_FAILURES);
export type RewardSendFailure = z.infer<typeof rewardSendFailureSchema>;

// A single earn rule: a verified target behaviour awards a fixed point value.
// `behavior` is drawn from the ledger's operation-subtype vocabulary so config
// and ledger speak the same names.
export const rewardEarnRuleSchema = z
  .object({
    behavior: rewardOperationSubtypeSchema,
    points: z.number().int().nonnegative(),
    // Per-earn clamp: a single earn credits `min(points, daily_max)`. Absent ⇒
    // no clamp. This is the behaviour-scoped successor to the schedule-level
    // `caps.daily_points_max`, so a high-value behaviour (an attended
    // appointment) is never clipped by the check-in's small cap. It clamps one
    // earn, not a day's accumulation across earns.
    daily_max: z.number().int().nonnegative().optional(),
  })
  .strict();
export type RewardEarnRule = z.infer<typeof rewardEarnRuleSchema>;

// `reward_schedules.config` — validated at the repo boundary (ADR-022) wherever
// a schedule row is read. `.strict()` rejects unknown keys rather than silently
// stripping, so a malformed catalog row fails loudly. The daily cap now lives
// per-earn-rule as `earn_rules[].daily_max`; `caps.daily_points_max` is retained
// as OPTIONAL only for the ADR-056 expand window (not-yet-migrated rows still
// carry it, and the previous release's code still reads it) — the contract that
// drops it and re-tightens the shape is tracked separately. `caps.max_points_balance`
// is the optional lifetime earn-side clamp the earn engine clips against
// (nullable/absent ⇒ no lifetime cap).
export const RewardScheduleConfig = z
  .object({
    earn_rules: z.array(rewardEarnRuleSchema),
    caps: z
      .object({
        daily_points_max: z.number().int().nonnegative().optional(),
        max_points_balance: z.number().int().nonnegative().optional(),
      })
      .strict()
      .optional(),
    // Redemption-side yearly cap (points a member may redeem per member-local
    // calendar year), seeded per programme. Optional so a schedule with no cap
    // configured parses cleanly; the redemption path also reads it raw
    // (`config->>'max_yearly_redemption'`) and MAXes across active programmes.
    // Absent ⇒ that programme contributes no cap. Lives here (not under `caps`,
    // which is the earn-side clamp) because it gates redemption, not earning.
    max_yearly_redemption: z.number().int().nonnegative().optional(),
  })
  .strict();
export type RewardScheduleConfig = z.infer<typeof RewardScheduleConfig>;

// ─── wire schemas ───────────────────────────────────────────────────────────

// One "way to earn" as surfaced to the member: a configured behaviour and the
// points it awards. Derived from the member's main-programme
// `reward_schedules.config.earn_rules` (zero/absent behaviours omitted). The
// wire key is `behaviour` (member-facing spelling); the config/ledger enum key
// is `behavior` — the projection maps between them.
export const rewardsWaysToEarnEntrySchema = z.object({
  behaviour: rewardOperationSubtypeSchema,
  points: z.number().int().nonnegative(),
});
export type RewardsWaysToEarnEntry = z.infer<typeof rewardsWaysToEarnEntrySchema>;

// `GET /api/v1/rewards/status` — the sole member-facing rewards read. NO `tier`
// field (there is no substance tier). `pointsBalance` is the SQL `SUM(amount)`
// ledger aggregate and always reflects the true sum regardless of enrolment
// state (a not-enrolled member with a pre-reshape historical balance still sees
// it, matching the home surface). `enrolmentEnabled` reflects the
// `flag-rewards-enrolment` evaluation. `isVisible` has no writer this ticket
// (defaults true; only admin visibility control, out of scope, sets it false).
// The member's yearly-redemption headroom: `spentPoints` is the member-local
// calendar-year debit total, `maxPoints` the effective cap. `maxPoints` is null
// when the effective cap is unset (a misconfiguration — no active programme
// carries a configured cap and there is no override), so the client can render
// "no limit configured" rather than a spurious zero.
export const rewardsYearlyRedemptionSchema = z.object({
  spentPoints: z.number(),
  maxPoints: z.number().nullable(),
});
export type RewardsYearlyRedemption = z.infer<typeof rewardsYearlyRedemptionSchema>;

// What a check-in submitted RIGHT NOW would earn this member. `points` is what
// the loop is worth (kept when capped or already claimed today, `0` when
// nothing resolves); `availableToday` is whether to promise it. A client
// promises only when both hold. Not a per-entry flag on `waysToEarn`: the
// appointment behaviours there have no day key to answer availability with.
export const rewardsCheckInEarnSchema = z.object({
  points: z.number().int().nonnegative(),
  availableToday: z.boolean(),
});
export type RewardsCheckInEarn = z.infer<typeof rewardsCheckInEarnSchema>;

// ADR-135 Rule 2 — this adds `yearlyRedemption` to an EXISTING client-parsed
// response (`GET /rewards/status`, shipped earn-side), so installed bundles
// already call it. Option (a) is satisfied, not merely asserted: this schema is
// a plain non-strict `z.object`, and always has been (it never carried
// `.strict()`), so a Zod parser strips unknown keys by default. Every installed
// bundle — pre- and post-tolerant-reader — therefore drops the new field rather
// than rejecting the whole response. The two prod outages ADR-135 cites were
// `.strict()` response schemas; this one cannot fail that way. Do NOT add
// `.strict()` here (Rule 1) — that would break every bundle in the field.
export const rewardsStatusDataSchema = z.object({
  enrolled: z.boolean(),
  isVisible: z.boolean(),
  pointsBalance: z.number(),
  waysToEarn: z.array(rewardsWaysToEarnEntrySchema),
  enrolmentEnabled: z.boolean(),
  yearlyRedemption: rewardsYearlyRedemptionSchema,
  // ADR-135: additive and safe for old clients because neither this schema nor
  // `rewardsCheckInEarnSchema` is strict (Rule 1 strictness nests). Optional
  // (Rule 6) because prod `mobile-publish` does not wait for the API deploy, and
  // a required field missing from an older server would fail the whole status
  // parse, not just the pill.
  checkInEarn: rewardsCheckInEarnSchema.optional(),
  // ADR-135 Rule 2 on the same terms as `yearlyRedemption` above: installed
  // bundles strip this key. Optional because a bundle can be newer than the
  // server it reads — the prod OTA publish does not wait for the API deploy —
  // and a missing value then means "judge by `enrolled`", as before the field.
  eligibilityCmCheckFailed: z.boolean().optional().meta({
    description:
      'True only when the CM eligibility check could not be completed for a member who is not yet enrolled, so `enrolled: false` is not a definite answer. False otherwise, including for every enrolled member.',
  }),
});
export type RewardsStatusData = z.infer<typeof rewardsStatusDataSchema>;
export const rewardsStatusResponseSchema = apiEnvelope(rewardsStatusDataSchema);
export type RewardsStatusResponse = z.infer<typeof rewardsStatusResponseSchema>;

// The read-only CM-eligibility answer: whether the member would be enrolled in
// rewards right now, without enrolling them. `surface` names the one caller
// that may ask, so the server can count the answers per surface; it is a
// closed request vocabulary, never free text.
export const REWARDS_CM_ELIGIBILITY_SURFACES = ['onboarding'] as const;
export const rewardsCmEligibilitySurfaceSchema = z.enum(REWARDS_CM_ELIGIBILITY_SURFACES);
export type RewardsCmEligibilitySurface = z.infer<typeof rewardsCmEligibilitySurfaceSchema>;
export const rewardsCmEligibilityQuerySchema = z
  .object({ surface: rewardsCmEligibilitySurfaceSchema })
  .strict();
export type RewardsCmEligibilityQuery = z.infer<typeof rewardsCmEligibilityQuerySchema>;
// A client-parsed response: left non-strict so a later additive field never
// fails an installed bundle's parse (ADR-135 Rule 1).
export const rewardsCmEligibilityDataSchema = z.object({ eligible: z.boolean() });
export type RewardsCmEligibilityData = z.infer<typeof rewardsCmEligibilityDataSchema>;
export const rewardsCmEligibilityResponseSchema = apiEnvelope(rewardsCmEligibilityDataSchema);
export type RewardsCmEligibilityResponse = z.infer<typeof rewardsCmEligibilityResponseSchema>;

export const rewardLedgerEntrySchema = z.object({
  amount: z.number(),
  operationType: rewardOperationTypeSchema,
  // Deliberately `z.string()`, NOT the strict `rewardOperationSubtypeSchema`
  // enum: this is the member-facing READ wire, and a client must degrade
  // gracefully on a subtype the server adds before the client updates. A closed
  // enum here would reject the whole page, so the mobile category registry's
  // unknown→fallback (its own `resolveRewardCategory`) could never run. The
  // strict enum still governs the config/earn/write paths.
  operationSubtype: z.string(),
  sourceRef: z.string(),
  createdAt: z.string(),
});
export type RewardLedgerEntry = z.infer<typeof rewardLedgerEntrySchema>;

// The ledger endpoint's unwrapped data body — the single source of truth for
// the list shape, so clients parse this rather than re-wrapping the element
// schema locally (ADR-066).
export const rewardLedgerDataSchema = z.array(rewardLedgerEntrySchema);
export type RewardLedgerData = z.infer<typeof rewardLedgerDataSchema>;

export const rewardLedgerResponseSchema = apiEnvelope(rewardLedgerDataSchema);
export type RewardLedgerResponse = z.infer<typeof rewardLedgerResponseSchema>;

// ─── paginated ledger read (member-facing history) ───────────────────────────
//
// Keyset (cursor) pagination over the append-only ledger: newest-first, seeking
// on `(created_at, source_ref)` so a row inserted between page fetches never
// duplicates or skips an entry at a page boundary (an offset window would). The
// `cursor` is an opaque server-encoded token of the last row's `(createdAt,
// sourceRef)`; clients treat it as a black box and echo the prior page's
// `nextCursor`. `operationType` narrows the page to earns (`reward`) or
// redemptions (`redemption`); omitted returns both.
export const REWARD_LEDGER_PAGE_LIMIT_DEFAULT = 20;
export const REWARD_LEDGER_PAGE_LIMIT_MAX = 50;

export const rewardLedgerQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .max(REWARD_LEDGER_PAGE_LIMIT_MAX)
    .default(REWARD_LEDGER_PAGE_LIMIT_DEFAULT),
  operationType: rewardOperationTypeSchema.optional(),
});
export type RewardLedgerQuery = z.infer<typeof rewardLedgerQuerySchema>;

// A history-page entry is a ledger entry plus the member's IANA zone in effect at
// `createdAt` (ADR-095 Rule 10), decided server-side from the member's zone
// history — NOT the device zone. `createdAt` is a UTC instant; the client formats
// it in THIS zone, so a member who travels (or relocated after earning) still
// sees each earn under the calendar day it belonged to when earned, not one
// shifted by their current device offset. Resolved per entry because a relocated
// member's older earns can fall under a different zone than their recent ones.
// Kept separate from `rewardLedgerEntrySchema` so the point/range `/ledger` read
// (the celebration/appointment surfaces) is unaffected — only history carries a zone.
// `ianaTimezoneSchema` (a refined, supported-IANA string — ADR-095 Rule 1), not a
// bare string, so a malformed zone is rejected at the boundary rather than
// silently degrading a history date to the raw timestamp.
export const rewardHistoryEntrySchema = rewardLedgerEntrySchema.extend({
  zone: ianaTimezoneSchema,
});
export type RewardHistoryEntry = z.infer<typeof rewardHistoryEntrySchema>;

export const rewardLedgerPageDataSchema = z.object({
  entries: z.array(rewardHistoryEntrySchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});
export type RewardLedgerPageData = z.infer<typeof rewardLedgerPageDataSchema>;

export const rewardLedgerPageResponseSchema = apiEnvelope(rewardLedgerPageDataSchema);
export type RewardLedgerPageResponse = z.infer<typeof rewardLedgerPageResponseSchema>;

// Whether the member runs any rewards (contingency-management) loop: derived
// from the presence of a reward schedule on one of their active programs.
export const REWARD_STATUS = ['enabled', 'disabled'] as const;
export const rewardStatusSchema = z.enum(REWARD_STATUS);
export type RewardStatus = z.infer<typeof rewardStatusSchema>;

// A ledger entry as shown in the staff summary — the element shape plus the
// program it was earned under (parsed from `source_ref`; null when it can't be
// attributed). The row carries both the program `key` and its `displayName` so
// it is self-describing: a historical earn from a now-inactive program (absent
// from the active `programs[]` breakdown) still renders its program name.
// Staff-summary ledger row (Atlas). Deliberately NOT derived from the reshaped
// member-facing `rewardLedgerEntrySchema`: it keeps the legacy `entryType`
// (derived server-side from `operation_type`) so the Atlas panel wire is stable
// across the signed-ledger reshape — no Atlas change. Not drift-guarded against
// the table (the member ledger carries that guard).
export const rewardsSummaryLedgerEntrySchema = z.object({
  amount: z.number(),
  entryType: rewardEntryTypeSchema,
  sourceRef: z.string(),
  createdAt: z.string(),
  // The zone in effect for the member WHEN this entry happened, per ADR-095
  // Rule 10 — read from `member_timezone_history`, never the member's current
  // zone. A member who relocated would otherwise have their older entries
  // re-bucketed into the wrong calendar day.
  zone: ianaTimezoneSchema,
  programKey: z.string().nullable(),
  programName: z.string().nullable(),
  // The ledger row's `operation_subtype`, so staff can tell a check-in from an
  // appointment reward, a staff grant or an import. An open string rather than
  // the ledger CHECK enum: a subtype added server-side later would otherwise fail
  // the whole panel's parse instead of rendering with a fallback label.
  // Optional because the field may be genuinely absent from an older API.
  operationSubtype: z.string().optional(),
  // True when the row came from a Pelago 1.0 import (decided server-side from its
  // `source_ref`), so the client never parses `source_ref` itself.
  imported: z.boolean().optional(),
});
export type RewardsSummaryLedgerEntry = z.infer<typeof rewardsSummaryLedgerEntrySchema>;

// Per-program rewards picture: whether that program earns (has a schedule), what
// one check-in actually mints under it (`earnRulePoints` — the cap-clamped
// resolution, not the raw rule, so it never disagrees with the ledger) plus the
// optional daily earn cap from its schedule config, and the all-time points
// earned under it.
export const rewardsProgramSummarySchema = z.object({
  key: z.string(),
  displayName: z.string(),
  isMain: z.boolean(),
  enabled: z.boolean(),
  earnRulePoints: z.number().nullable(),
  dailyEarnCap: z.number().nullable(),
  earnedTotal: z.number(),
});
export type RewardsProgramSummary = z.infer<typeof rewardsProgramSummarySchema>;

// A redemption ORDER as the staff panel renders it — the order-level truth, not a
// ledger projection. Deliberately a closed field list: `vendor_attempt_at` is read
// server-side to derive `refundable` and MUST NOT travel, because a raw send marker
// on a staff response invites a client to re-derive the refund predicate itself, and
// the delivery email, recipient name, gift-card code and `fulfilment_data` are member
// PII or bearer-equivalent secrets the panel has no use for (Atlas 1 renders none of
// them either, so omitting them is parity with what staff actually see).
//
// `status` is the closed `rewardRedemptionStatusSchema` rather than an open string.
// That is ADR-135-safe here for the same reason `REWARD_ENTRY_TYPE` is: this wire is
// read only by the Atlas staff SPA, deployed in lockstep with the API, never by a
// pre-tolerance mobile bundle. The member-facing ledger keeps its open `z.string()`.
/**
 * How many redemption ORDERS the staff summary carries. Shared rather than
 * declared per side: the API bounds the read with it and the panel says so when
 * a member is at the bound, and a silent disagreement between those two numbers
 * is exactly the "quietly truncated" failure the cap is meant to be honest about.
 */
export const STAFF_REDEMPTION_LIMIT = 50;

export const rewardsSummaryRedemptionSchema = z.object({
  id: z.string(),
  // Denormalized and NOT NULL on `reward_redemptions`, so the display name needs no
  // product join and survives the product being deactivated after the order.
  productName: z.string(),
  pointsSpent: z.number().int(),
  status: rewardRedemptionStatusSchema,
  createdAt: z.string(),
  // The zone in effect when the order was placed (ADR-095 Rule 10), not the
  // member's current one.
  zone: ianaTimezoneSchema,
  // Pre-send: `status='processing'` AND `vendor_attempt_at IS NULL` — the exact
  // predicate `claimForRefund` compare-and-sets on. An advisory hint computed at
  // read time, NOT a guarantee: the fulfilment consumer may claim the row between
  // this read and the operator's click, which is why the refund endpoint still
  // answers 409 and the panel treats that as an expected race.
  refundable: z.boolean(),
});
export type RewardsSummaryRedemption = z.infer<typeof rewardsSummaryRedemptionSchema>;

// Composite staff read: the whole rewards picture for a member in one body,
// aggregated server-side so a single care-team panel resolves it in one
// round-trip. `balance` is the net derived aggregate; `totalEarned` /
// `totalRedeemed` are all-time sums (Σ earned / Σ issued); `earnedToday` feeds
// the daily-earn-cap progress. Reuses the ledger element schema (ADR-066).
//
// Atlas renders neither `status`, `earnedToday` nor `programs` since the
// rewards-tab redesign: its header reads `enrolled` + `isVisible`, and the
// per-programme breakdown was removed. They stay on the wire for compatibility;
// do not re-derive a status pill from `status`, use `enrolled` and `isVisible`.
export const rewardsSummaryDataSchema = z.object({
  status: rewardStatusSchema,
  balance: z.number(),
  totalEarned: z.number(),
  totalRedeemed: z.number(),
  earnedToday: z.number(),
  // The member's rewards-UI visibility (`reward_enrolments.is_visible`), the
  // control the staff visibility toggle reflects and sets. Defaults to `true`
  // for a member with no enrolment row (the column default).
  isVisible: z.boolean(),
  programs: z.array(rewardsProgramSummarySchema),
  ledger: z.array(rewardsSummaryLedgerEntrySchema),
  // The member's redemption ORDERS, newest-first and bounded — NOT scoped by the
  // route's `from`/`to`, which narrow the ledger only. Lifetime is parity with
  // backend2's `findRedemptionsByIdentityId`, which applies no date filter.
  redemptions: z.array(rewardsSummaryRedemptionSchema),
  // The yearly-redemption headroom, the same figure the member surface reads.
  // `maxPoints` is null when no active programme carries a configured cap and the
  // enrolment has no override — the panel renders "no limit configured" rather
  // than a bar at zero, which would read as a real cap with nothing spent.
  yearlyRedemption: rewardsYearlyRedemptionSchema,
  // The display names of the catalogs the member's enrolment snapshot assigns
  // (`reward_enrolments.catalog_keys`). Empty when the member has no enrolment row
  // or an empty snapshot; a snapshot key matching no catalog row is dropped.
  catalogs: z.array(z.string()),
  // The member's zone at the request instant, for display only ("dates are in the
  // member's local time"). Each ledger entry keeps its own `zone`; nothing reads
  // this one to bucket a date.
  zone: ianaTimezoneSchema.optional(),
  // Whether the member has a `reward_enrolments` row. `isVisible` defaults to
  // `true` without one, so this is what tells the panel a visibility write would
  // be refused as `not_enrolled`.
  enrolled: z.boolean().optional(),
});
export type RewardsSummaryData = z.infer<typeof rewardsSummaryDataSchema>;

export const rewardsSummaryResponseSchema = apiEnvelope(rewardsSummaryDataSchema);
export type RewardsSummaryResponse = z.infer<typeof rewardsSummaryResponseSchema>;

// ─── Redemption commerce contracts (ADR-061/066) ─────────────────────────────

// A Sendoso-shaped external reference entry: the `points` denomination this
// variant fulfils and its `productVariantId`. Fulfilment-only; the fulfilment
// consumer matches a redemption's `points_spent` against `points` to select the
// variant (backend2 parity — its `externalId.price` was already the points
// scale, so there is no separate USD value to carry).
export const rewardExternalRefEntrySchema = z.object({
  points: z.number().int(),
  productVariantId: z.string(),
});
export type RewardExternalRefEntry = z.infer<typeof rewardExternalRefEntrySchema>;

// The `reward.redemption.requested` event payload the fulfilment consumer reads.
// PHI-free by construction. Unknown keys are stripped rather than rejected: the
// consumer is a separate Lambda, so a strict parse would dead-letter every paid
// redemption published by a newer producer during a deploy. The producer's exact
// key set is pinned by tests instead. `product_key` / `product_name` are the
// product's catalog key and catalog title (optional so an event published before
// they existed still parses). `legacy_product_id` is the backend2 integer
// product id the lifecycle-marketing feed keys on: present only when the
// product carries one — a producer omits the key rather than sending null, so
// the forwarded event has no id field at all for a product backend2 never had.
// `status` is injected by buildAnnotatedDomainEvent from the analytics catalog —
// closed to that exact value, never free text.
export const redemptionRequestedPayloadSchema = z.object({
  redemption_id: z.string().uuid(),
  product_type: rewardProductTypeSchema,
  product_key: z.string().min(1).optional(),
  product_name: z.string().min(1).optional(),
  legacy_product_id: z.number().int().positive().optional(),
  status: z.literal('created').optional(),
});
export type RedemptionRequestedPayload = z.infer<typeof redemptionRequestedPayloadSchema>;

// The shape the fulfilment consumer/sweep writes into
// `reward_redemptions.fulfilment_data` (jsonb). `providerRef` is the vendor's
// send handle used to probe/reconcile a stuck order; `sendResult` / `sendDetails`
// are the provider-owned raw responses whose exact shape belongs to the concrete
// provider, so they stay `unknown` here (ADR-117). Declared as a bridge
// type rather than left bare jsonb (ADR-066).
export const rewardFulfilmentDataSchema = z.object({
  providerName: z.string(),
  providerRef: z.string(),
  sendResult: z.unknown().optional(),
  sendDetails: z.unknown().optional(),
});
export type RewardFulfilmentData = z.infer<typeof rewardFulfilmentDataSchema>;

// A catalog product as served by `GET /api/v1/rewards/products`. Prices are
// points-native: `pointsPrice` for a `gift_item`, the discrete `pointPriceRanges`
// (cap-pruned) for an `e_gift`. `affordable` is computed against the member's
// current balance. Display metadata (brand colour, logo, photos) lives in the
// mobile client's local map keyed by `key`.
export const rewardProductSchema = z.object({
  key: z.string(),
  title: z.string(),
  type: rewardProductTypeSchema,
  imageUrl: z.string().nullable(),
  pointsPrice: z.number().int().nullable(),
  pointPriceRanges: z.array(z.number().int()).nullable(),
  affordable: z.boolean(),
});
export type RewardProduct = z.infer<typeof rewardProductSchema>;

export const rewardProductsDataSchema = z.array(rewardProductSchema);
export type RewardProductsData = z.infer<typeof rewardProductsDataSchema>;
export const rewardProductsResponseSchema = apiEnvelope(rewardProductsDataSchema);
export type RewardProductsResponse = z.infer<typeof rewardProductsResponseSchema>;

// The order fields a redemption addresses: which product, at what discrete
// point denomination.
const rewardRedemptionOrderSchema = z.object({
  productKey: z.string(),
  productType: rewardProductTypeSchema,
  pointsToRedeem: z.number().int().positive(),
});

// `POST /redemption-review` request — a pre-flight over the ORDER only. Delivery
// details are optional here: review runs before the member has typed the
// recipient name (the confirm screen calls it to fetch the login-email prefill
// and the order summary), so recipient completeness + email format are enforced
// at submit, not review. The order rules (enrolment, visibility, active product,
// denomination, cap, balance) are validated either way.
export const rewardRedemptionReviewRequestSchema = rewardRedemptionOrderSchema.extend({
  deliveryEmail: z.string().optional(),
  deliveryFirstName: z.string().optional(),
  deliveryLastName: z.string().optional(),
});
export type RewardRedemptionReviewRequest = z.infer<typeof rewardRedemptionReviewRequestSchema>;

// `POST /redemptions` request — the order plus the complete, member-typed
// delivery details (email + recipient name; NO address — Sendoso collects
// shipping in its own claim flow) and the client-minted, per-attempt-stable
// idempotency key (a body field, not a header).
export const rewardRedemptionRequestSchema = rewardRedemptionOrderSchema.extend({
  deliveryEmail: z.string(),
  deliveryFirstName: z.string().min(1),
  deliveryLastName: z.string().min(1),
  idempotencyKey: z.string().uuid(),
});
export type RewardRedemptionRequest = z.infer<typeof rewardRedemptionRequestSchema>;

// `POST /redemption-review` response — the delivery prefill (KMP parity) and the
// order summary. `yearlyRemainingAfter` is null when the cap is unset.
//
// The recipient name is optional here while the email is not, and the direction
// that asymmetry protects is the one a reader gets backwards by default: a NEW
// client reading an OLD server. Mobile ships JS over the air on its own cadence,
// so a bundle can reach an API deployed minutes earlier; required fields would
// make that window a parse failure on a screen the member is standing on. The
// opposite direction needs nothing — this object is not `.strict()` and must not
// become so, so an installed bundle simply drops keys it does not know.
export const rewardRedemptionReviewDataSchema = z.object({
  deliveryEmail: z.string(),
  deliveryFirstName: z.string().optional(),
  deliveryLastName: z.string().optional(),
  remainingBalanceAfter: z.number(),
  yearlyRemainingAfter: z.number().nullable(),
});
export type RewardRedemptionReviewData = z.infer<typeof rewardRedemptionReviewDataSchema>;
export const rewardRedemptionReviewResponseSchema = apiEnvelope(rewardRedemptionReviewDataSchema);
export type RewardRedemptionReviewResponse = z.infer<typeof rewardRedemptionReviewResponseSchema>;

// A redemption order as returned by `POST /redemptions` and
// `GET /redemptions/{id}`. `status` reflects the real lifecycle (`processing`
// until the fulfilment consumer advances it).
//
// `productKey` is the ordered product's stable catalog key, so a
// client can pick product-specific post-redemption copy from the order itself
// rather than from a route param or a catalog fetch. Additive and optional: it
// is absent for an order with no `reward_products` row (a legacy import) and
// from an API that predates the field, and clients that ignore it are unaffected.
export const rewardRedemptionSchema = z.object({
  id: z.string(),
  status: rewardRedemptionStatusSchema,
  productKey: z.string().min(1).optional(),
  productName: z.string(),
  pointsSpent: z.number().int(),
  deliveryEmail: z.string(),
  createdAt: z.string(),
});
export type RewardRedemption = z.infer<typeof rewardRedemptionSchema>;
export const rewardRedemptionResponseSchema = apiEnvelope(rewardRedemptionSchema);
export type RewardRedemptionResponse = z.infer<typeof rewardRedemptionResponseSchema>;

// ─── member redemption history (`GET /redemptions`) ─────────────────────────
//
// The member's own orders, newest first, keyset-paginated on `(created_at, id)`
// with the same opaque-cursor contract as the ledger history read. Its own
// limits rather than the ledger's, so the two reads can diverge independently.
export const REWARD_REDEMPTION_HISTORY_PAGE_LIMIT_DEFAULT = 20;
export const REWARD_REDEMPTION_HISTORY_PAGE_LIMIT_MAX = 50;

export const rewardRedemptionHistoryQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .max(REWARD_REDEMPTION_HISTORY_PAGE_LIMIT_MAX)
    .default(REWARD_REDEMPTION_HISTORY_PAGE_LIMIT_DEFAULT),
});
export type RewardRedemptionHistoryQuery = z.infer<typeof rewardRedemptionHistoryQuerySchema>;

// One order as the member's history renders it — a projection of
// `reward_redemptions` plus the member's zone at `createdAt` (ADR-095 Rule 10).
// Tolerant where the server may grow: `status` is `z.string()`, not
// `rewardRedemptionStatusSchema`, so a status added later still parses on an
// installed build (ADR-135); `productName` is null when the order never had a
// name (an imported 1.0 order with no product), and the client shows its own
// localized fallback. `pointsSpent` is non-negative rather than positive: the
// column carries no positivity CHECK and the 1.0 import writes `|amount|`, so an
// imported `0` must not fail the whole page.
export const rewardRedemptionHistoryEntrySchema = z.object({
  id: z.string(),
  productName: z.string().nullable(),
  pointsSpent: z.number().int().nonnegative(),
  status: z.string(),
  createdAt: z.string(),
  zone: ianaTimezoneSchema,
});
export type RewardRedemptionHistoryEntry = z.infer<typeof rewardRedemptionHistoryEntrySchema>;

export const rewardRedemptionHistoryPageDataSchema = z.object({
  entries: z.array(rewardRedemptionHistoryEntrySchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});
export type RewardRedemptionHistoryPageData = z.infer<typeof rewardRedemptionHistoryPageDataSchema>;

export const rewardRedemptionHistoryPageResponseSchema = apiEnvelope(
  rewardRedemptionHistoryPageDataSchema,
);
export type RewardRedemptionHistoryPageResponse = z.infer<
  typeof rewardRedemptionHistoryPageResponseSchema
>;

// ─── Staff write endpoints (internal-rewards) ───────────────────────────────
//
// Three `staffBearer` + `atlas_enabled` writes. Request schemas are `.strict()`
// (ADR-135 — reject unknown keys on the way in); responses go through
// `apiEnvelope` and stay tolerant so a future additive field never breaks the
// Atlas client.

// The two staff-authored positive-credit subtypes (a subset of the earn
// vocabulary): a goodwill/compensation grant vs a correction of an erroneous
// entry. Both mint an `operation_type='reward'` (positive) ledger row.
export const rewardGrantSubtypeSchema = z.enum(['manual_reward', 'admin_adjustment']);
export type RewardGrantSubtype = z.infer<typeof rewardGrantSubtypeSchema>;

// `POST /internal/rewards/manual-reward` request. `points` is bounded at 10000
// (= $100) for backend2 parity; `idempotencyKey` (≤ 36 chars, minted once per
// Atlas form open) makes a double-submit collapse to a single credit.
export const internalManualRewardRequestSchema = z
  .object({
    memberId: memberIdSchema,
    points: z.number().int().positive().max(10000),
    // The mandatory audit justification: trimmed so whitespace-only cannot mint a
    // credit with an empty compliance record, and bounded so a large free-text
    // reason cannot push the audit event past the EventBridge size limit (which
    // would silently route it down the fail-open publish path).
    reason: z.string().trim().min(1).max(200),
    subtype: rewardGrantSubtypeSchema.default('manual_reward'),
    idempotencyKey: z.string().min(1).max(36),
  })
  .strict();
export type InternalManualRewardRequest = z.infer<typeof internalManualRewardRequestSchema>;

export const internalManualRewardDataSchema = z.object({
  credited: z.number().int(),
  outcome: z.enum(['credited', 'idempotent_duplicate']),
});
export type InternalManualRewardData = z.infer<typeof internalManualRewardDataSchema>;
export const internalManualRewardResponseSchema = apiEnvelope(internalManualRewardDataSchema);
export type InternalManualRewardResponse = z.infer<typeof internalManualRewardResponseSchema>;

// `PATCH /internal/rewards/visibility` request — the first writer for
// `reward_enrolments.is_visible`. UPDATE-only: a member with no enrolment row is
// refused (`409 not_enrolled`), never inserted.
export const internalRewardsVisibilityRequestSchema = z
  .object({
    memberId: memberIdSchema,
    isVisible: z.boolean(),
  })
  .strict();
export type InternalRewardsVisibilityRequest = z.infer<
  typeof internalRewardsVisibilityRequestSchema
>;

export const internalRewardsVisibilityDataSchema = z.object({
  isVisible: z.boolean(),
});
export type InternalRewardsVisibilityData = z.infer<typeof internalRewardsVisibilityDataSchema>;
export const internalRewardsVisibilityResponseSchema = apiEnvelope(
  internalRewardsVisibilityDataSchema,
);
export type InternalRewardsVisibilityResponse = z.infer<
  typeof internalRewardsVisibilityResponseSchema
>;

// `POST /internal/rewards/redemptions/{id}/refund` — no body; the redemption is
// named by the path param. The response reports the cancelled order status and
// the refunded points; an idempotent re-invocation returns the same shape.
export const internalRedemptionRefundParamsSchema = z.object({
  id: z.string().uuid(),
});
export type InternalRedemptionRefundParams = z.infer<typeof internalRedemptionRefundParamsSchema>;

export const internalRedemptionRefundDataSchema = z.object({
  status: rewardRedemptionStatusSchema,
  refunded: z.number().int(),
  outcome: z.enum(['refunded', 'idempotent_duplicate']),
});
export type InternalRedemptionRefundData = z.infer<typeof internalRedemptionRefundDataSchema>;
export const internalRedemptionRefundResponseSchema = apiEnvelope(
  internalRedemptionRefundDataSchema,
);
export type InternalRedemptionRefundResponse = z.infer<
  typeof internalRedemptionRefundResponseSchema
>;
